import { AsyncValidatorFn, ValidatorFn, Validators } from '@angular/forms';
import {
  ANGULAR_NATIVE_VALIDATORS_KEY,
  EMAIL_VALIDATOR_KEY,
  MAX_LENGTH_VALIDATOR_KEY,
  MAX_VALIDATOR_KEY,
  MIN_CHECKED_KEY,
  MIN_LENGTH_VALIDATOR_KEY,
  MIN_VALIDATOR_KEY,
  NULL_VALIDATOR_KEY,
  PASSWORD_KEY,
  PATTERN_VALIDATOR_KEY,
  REQUIRED_TRUE_VALIDATOR_KEY,
  REQUIRED_VALIDATOR_KEY,
  WidgetValidator
} from '@pt/pt-models';
import { CustomValidator } from './custom-validator';

export class ValidatorGenerator {

  public static generateValidator(widgetValidator: WidgetValidator[]): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (!!widgetValidator && widgetValidator.length > 0) {
      widgetValidator
        .forEach((v: WidgetValidator) => {
          const validatorFn = ValidatorGenerator.convertToValidatorFn(v);
          if (validatorFn) {
            validators.push(validatorFn);
          }
        });
    }
    return validators;
  }

  public static generateAsyncValidator(widgetValidator: WidgetValidator[]): AsyncValidatorFn[] {
    const validators: AsyncValidatorFn[] = [];
    if (!!widgetValidator && widgetValidator.length > 0) {
      widgetValidator
        .forEach((v: WidgetValidator) => {
          const validatorFn = ValidatorGenerator.convertToAsyncValidatorFn(v);
          if (validatorFn) {
            validators.push(validatorFn);
          }
        });
    }
    return validators;
  }

  public static generateFromGroupValidator(widgetValidator: WidgetValidator[]): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (!!widgetValidator && widgetValidator.length > 0) {
      widgetValidator
        .forEach((v: WidgetValidator) => {
          const validatorFn = ValidatorGenerator.convertToFormGroupValidatorFn(v);
          if (validatorFn) {
            validators.push(validatorFn);
          }
        });
    }
    return validators;
  }

  public static generateWidgetValidator(name: string, args?: any[], code?: any): WidgetValidator | null {
    if (name) {
      const widgetValidator = {
        key: name,
        args,
        code,
        enabled: true
      } as WidgetValidator;
      // Mi assicuro che il validatore sia valido quindi convertibile
      if (
        ValidatorGenerator.convertToValidatorFn(widgetValidator)
        ||
        name === PASSWORD_KEY
      ) {
        return widgetValidator;
      }
    }
    return null;
  }

  static requireCode(validator: WidgetValidator): boolean {
    return !!validator && !ANGULAR_NATIVE_VALIDATORS_KEY.find((key) => key === validator.key);
  }

  static requireArgs(validator: WidgetValidator): boolean {
    if (validator) {
      switch (validator.key) {
        case REQUIRED_VALIDATOR_KEY:
        case REQUIRED_TRUE_VALIDATOR_KEY:
        case EMAIL_VALIDATOR_KEY:
        case NULL_VALIDATOR_KEY:
          return false;
      }
    }
    return true;
  }

  static isNativeValidator(validator: WidgetValidator) {
    if (validator) {
      switch (validator.key) {
        case REQUIRED_VALIDATOR_KEY:
        case REQUIRED_TRUE_VALIDATOR_KEY:
        case EMAIL_VALIDATOR_KEY:
        case NULL_VALIDATOR_KEY:
        case MIN_LENGTH_VALIDATOR_KEY:
        case MAX_LENGTH_VALIDATOR_KEY:
        case MIN_VALIDATOR_KEY:
        case MAX_VALIDATOR_KEY:
        case PATTERN_VALIDATOR_KEY:
          return true;
      }
    }
    return false;
  }

  static validatorsContainsRequired(validators: WidgetValidator[]) {
    return validators && !!validators?.find(validatorKey => validatorKey.key === REQUIRED_VALIDATOR_KEY);
  }

  private static convertToValidatorFn(widgetValidator: WidgetValidator): ValidatorFn | null {
    if (!!widgetValidator && !!widgetValidator.key) {
      switch (widgetValidator.key) {
        case REQUIRED_VALIDATOR_KEY:
          return Validators.required;
        case REQUIRED_TRUE_VALIDATOR_KEY:
          return Validators.requiredTrue;
        case EMAIL_VALIDATOR_KEY:
          return Validators.pattern(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/);
        case NULL_VALIDATOR_KEY:
          return Validators.nullValidator;
        case MIN_LENGTH_VALIDATOR_KEY:
          if (!!widgetValidator.args && widgetValidator.args.length === 1) {
            return Validators.minLength(widgetValidator.args[0]);
          }
          break;
        case MAX_LENGTH_VALIDATOR_KEY:
          if (!!widgetValidator.args && widgetValidator.args.length === 1) {
            return Validators.maxLength(widgetValidator.args[0]);
          }
          break;
        case MIN_VALIDATOR_KEY:
          if (!!widgetValidator.args && widgetValidator.args.length === 1) {
            return Validators.min(widgetValidator.args[0]);
          }
          break;
        case MAX_VALIDATOR_KEY:
          if (!!widgetValidator.args && widgetValidator.args.length === 1) {
            return Validators.max(widgetValidator.args[0]);
          }
          break;
        case PATTERN_VALIDATOR_KEY:
          if (!!widgetValidator.args && widgetValidator.args.length === 1) {
            return Validators.pattern(widgetValidator.args[0]);
          }
          break;
      }
    }
    return null;
  }

  private static convertToAsyncValidatorFn(widgetValidator: WidgetValidator): AsyncValidatorFn | null {
    // if (!!widgetValidator && !!widgetValidator.key) {
    //   switch (widgetValidator.key) {
    //     default:
    //       console.warn('Custom validator not implemented!');
    //   }
    // }
    return null;
  }

  private static convertToFormGroupValidatorFn(widgetValidator: WidgetValidator): ValidatorFn | null {
    if (!!widgetValidator && !!widgetValidator.key) {
      switch (widgetValidator.key) {
        case MIN_CHECKED_KEY:
          return CustomValidator.widgetCheckboxLimit(widgetValidator.args);
      }
    }
    return null;
  }

}
